<template>
  <div class="grid-default -mt-[100px] bg-neutral-100 pt-[100px]">
    <div class="col-span-6 lg:col-span-12 lg:mb-8">
      <NuxtLink :to="localePathByType('PageRecord', shopSlug.path)">
        <Icon class="rotate-180" size="xs" name="arrow-right" :color="'#314863'" />
        <span class="relative top-[2px] ml-2 text-base font-normal text-black">
          {{ $t('Back') }}</span
        >
      </NuxtLink>
    </div>
    <div class="col-span-6 mt-6 lg:hidden">
      <h1 class="mb-2 text-3xl font-light text-blue-950 lg:text-6xl">{{ data.title }}</h1>
      <div v-if="data.price" class="mb-6 text-xl font-normal text-blue-950 lg:mb-8 lg:text-3xl">
        {{ data.price }} €
      </div>
    </div>
    <div class="col-span-6 flex h-fit items-center rounded-2xl bg-white lg:mb-20">
      <Swiper
        class="product-image-swiper h-[400px] lg:h-[780px]"
        :modules="modules"
        :slides-per-view="1"
        :navigation="true"
        :pagination="paginationOptions"
        @swiper="onSwiper"
      >
        <SwiperSlide v-for="(image, index) in data.images" :key="index" class="">
          <div class="flex h-full w-full scale-125 items-center justify-center rounded-2xl">
            <Image
              class="rounded-3xl object-cover"
              :lazy="true"
              :src="image.url"
              :alt="image.alt"
              :width="image.responsiveImage.width ? image.responsiveImage.width : image.width"
              :height="image.responsiveImage.height ? image.responsiveImage.height : image.height"
            />
          </div>
        </SwiperSlide>
      </Swiper>
    </div>
    <div class="col-span-6 lg:px-8">
      <div class="hidden lg:block">
        <h1 class="font-light text-blue-950 lg:mb-2 lg:text-6xl">{{ data.title }}</h1>
        <div v-if="data.price" class="font-normal text-blue-950 lg:mb-8 lg:text-3xl">
          {{ data.price }} €
        </div>
      </div>
      <div class="flew-row my-10 flex gap-2 lg:mt-2 lg:gap-4">
        <div
          v-for="(image, index) in data.images"
          :key="index"
          class="flex h-20 w-20 items-center justify-center rounded-3xl bg-white p-3"
          :class="activeSlide === index ? 'border border-neutral-500' : 'border border-white'"
        >
          <Image
            class="m-auto"
            :lazy="true"
            :src="image.url"
            :alt="image.alt"
            :width="image.width"
            :height="image.height"
            @click="goToSlide(index)"
          />
        </div>
      </div>
      <div v-if="variations && variations[0]" class="my-10">
        <div class="text-sm font-normal text-neutral-500">{{ $t('Variations') }}</div>
        <div class="mt-4 flex flex-row flex-wrap lg:gap-1">
          <div
            v-for="(variation, index) in variations"
            :key="index"
            class="mr-1 mt-1 cursor-pointer rounded-full border-4 border-light-gray px-6 py-3 lg:px-6 lg:py-4"
            @click="selectVariation($event, variation)"
          >
            {{ variation }}
          </div>
        </div>
      </div>
      <!-- <AddToCart
        :out-of-stock="!selectedProduct?.totalStock || selectedProduct?.totalStock <= 0"
        :variation="selectedVariation"
        :data="data"
      /> -->
      <Button :label="$t('inquiryForm.label')" icon :to="$t('inquiryForm.route')" />
      <div class="mb-4 mt-10 lg:mb-6">
        <div class="text-2xl font-normal text-blue-950">{{ $t('Description') }}</div>
        <div class="mt-4 text-base font-normal text-neutral-500" v-html="data.description"></div>
      </div>
      <div
        v-for="(info, index) in data.productFeatureOrDescription"
        :key="index"
        class="border-b-2 border-gray-500 py-5 text-base font-normal text-blue-950"
      >
        <span class="font-semibold">{{ info.feature }}: </span
        ><span clasS="prose lg:prose" v-html="info.descriptionOrValue" />
      </div>
      <div v-if="data.additionalProductLink" class="mt-4 lg:mt-6">
        <span class="font-semibold text-blue-950"
          >{{ $t('shop.requiredAdditionalProduct') }}:&nbsp;</span
        >
        <NuxtLink
          class="link"
          :to="localePathByType('ShopProductRecord', data.additionalProductLink?.slug as string)"
        >
          {{ data.additionalProductLink.title }}
        </NuxtLink>
      </div>
    </div>
    <div v-if="data.body" class="col-span-6 mt-6 lg:col-span-12 lg:mt-8">
      <StructuredText :data="data.body" class="prose lg:prose" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { paths } from '~/lib/paths'
import { ModelApiKey } from '~/types/models'
import type { LocaleCode } from '~/types/locales'

import 'swiper/css'
import 'swiper/css/pagination'

const { locale } = useI18n()
const { localePathByType } = useLocalePathByType()
const shopSlug = paths[locale.value as LocaleCode][ModelApiKey.ShopCategory]

const props = defineProps({
  data: {
    type: Object as PropType<any>,
    default: () => ({})
  }
})

const variations = ref(props.data.variation.split(','))
const paginationOptions = {
  clickable: true
}
const modules = [Navigation, Pagination]

const swiperInstance = ref()
const activeSlide = ref(0)
const selectedVariation = ref('')
// const selectedProduct = ref()

// async function fetchProductItem() {
//   const payload = {
//     id: props.data.id
//   }

//   const { data, error } = await useFetch('/.netlify/functions/snipcart-item', {
//     headers: { 'Content-type': 'application/json' },
//     method: 'POST',
//     body: toRaw(payload)
//   })

//   if (data.value) {
//     selectedProduct.value = JSON.parse(data.value)
//   }

//   if (error.value) {
//     console.log('error:', error.value)
//   }
// }

function onSwiper(swiper) {
  swiperInstance.value = swiper
}

function goToSlide(position: number) {
  swiperInstance.value?.slideTo(position)
  activeSlide.value = position
}

function selectVariation(event, variation) {
  if (!event.target) {
    return
  }

  for (let i = 0; i < event.target.parentElement.children.length; i++) {
    event.target.parentElement.children[i].classList.remove('bg-orange')
    event.target.parentElement.children[i].classList.remove('border-orange')
  }

  event.target.classList.toggle('bg-orange')
  event.target.classList.toggle('border-orange')

  selectedVariation.value = variation
}

onMounted(() => {
  swiperInstance.value?.on('slideChange', () => {
    activeSlide.value = swiperInstance.value?.activeIndex
  })
})
</script>

<style lang="postcss">
.product-image-swiper .swiper-pagination {
  position: absolute !important;
  left: 50% !important;
  transform: translateX(-50%) !important;
  bottom: 20px !important;
}

.swiper-pagination-bullet {
  --swiper-pagination-bullet-width: 12px;
  --swiper-pagination-bullet-size: 12px;
  --swiper-theme-color: theme(colors.blue.700);
  --swiper-pagination-bullet-inactive-color: theme(colors.blue.100);
  --swiper-pagination-bullet-opacity: 1;
  --swiper-pagination-bullet-horizontal-gap: 8px;
  --swiper-pagination-bullet-vertical-gap: 16px;
  @apply !rounded-full !opacity-100;
}
</style>
